import { useMemo, useState } from 'react'

import { useChatAttachments } from 'src/components/chat'

import { sendWithFiles } from './sendWithFiles'
import { resolveSquadFileUrl, squadChatFilesApi } from './squadFilesApi'
import type { ChatSendOptions } from './useChatSession'

type Params = {
  agentId: string | null
  appToken: string
  send: (prompt: string, options?: ChatSendOptions) => Promise<void>
  restoreDraft: (prompt: string) => void
}

/** Files staged in the home chat composer. Switching workspace swaps the app
 * token, which discards what the previous workspace's composer held. */
export const useHomeChatFiles = ({
  agentId,
  appToken,
  send,
  restoreDraft
}: Params) => {
  const api = useMemo(
    () => squadChatFilesApi(agentId ?? '', appToken),
    [agentId, appToken]
  )
  const resolveUrl = useMemo(() => resolveSquadFileUrl(appToken), [appToken])
  const attachments = useChatAttachments(api)
  const [sendingIds, setSendingIds] = useState<string[]>([])
  const items = attachments.items.filter(
    (item) => !sendingIds.includes(item.localId)
  )
  const hasReady = items.some((item) => item.status === 'ready')

  const unhide = (ids: string[]) =>
    setSendingIds((current) => current.filter((id) => !ids.includes(id)))

  const submit = (prompt: string) => {
    if (attachments.isUploading) return

    void sendWithFiles(prompt, {
      items,
      hide: (ids) => setSendingIds((current) => [...current, ...ids]),
      unhide,
      release: attachments.release,
      send,
      restoreDraft
    })
  }

  return {
    items,
    add: attachments.add,
    remove: attachments.remove,
    retry: attachments.retry,
    discardAll: attachments.discardAll,
    isUploading: attachments.isUploading,
    hasReady,
    submit,
    resolveUrl
  }
}
